import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, Space, Typography, message, Spin, InputNumber } from 'antd';
import { SaveOutlined, InfoCircleOutlined, PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { aboutAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function AboutPage() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [dataId, setDataId] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await aboutAPI.get();
            if (response.data) {
                form.setFieldsValue(response.data);
                setDataId(response.data.id || response.data._id);
            }
        } catch (error) {
            console.error('Error fetching about data:', error);
            message.error('Failed to fetch about data');
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            if (dataId) {
                await aboutAPI.update(dataId, values);
                message.success('About information updated successfully!');
            } else {
                const response = await aboutAPI.create(values);
                setDataId(response.data.id || response.data._id);
                message.success('About information created successfully!');
            }
            fetchData();
        } catch (error) {
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}><InfoCircleOutlined /> About Company</Title>
                <Text type="secondary">Manage company history and achievements</Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ history: [{ year: '', event: '' }], achievements: [{ title: '', description: '', icon: '' }] }}>
                {/* Basic Info */}
                <Card title="📄 Basic Information" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="title" label="Page Title" rules={[{ required: true }]}>
                        <Input size="large" placeholder="About Us" />
                    </Form.Item>
                    <Form.Item name="content" label="Content" rules={[{ required: true }]}>
                        <TextArea rows={6} placeholder="Tell your company story..." />
                    </Form.Item>
                </Card>

                {/* History */}
                <Card title="📅 Company History" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="history">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Card key={field.key} size="small" style={{ marginBottom: 16, background: '#f5f5f5' }} title={`History ${index + 1}`}>
                                        <Space style={{ display: 'flex', width: '100%' }} align="baseline">
                                            <Form.Item {...field} name={[field.name, 'year']} label="Year" rules={[{ required: true }]} style={{ width: 120 }}>
                                                <InputNumber size="large" min={1900} max={2100} placeholder="2020" style={{ width: '100%' }} />
                                            </Form.Item>
                                            <Form.Item {...field} name={[field.name, 'event']} label="Event" rules={[{ required: true }]} style={{ flex: 1 }}>
                                                <Input size="large" placeholder="Company milestone or event" />
                                            </Form.Item>
                                        </Space>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} block>
                                                Remove History
                                            </Button>
                                        )}
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add History
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Achievements */}
                <Card title="�� Achievements" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.List name="achievements">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Card key={field.key} size="small" style={{ marginBottom: 16, background: '#f5f5f5' }} title={`Achievement ${index + 1}`}>
                                        <Form.Item {...field} name={[field.name, 'title']} label="Title" rules={[{ required: true }]}>
                                            <Input size="large" placeholder="e.g., ISO 9001 Certified" />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'description']} label="Description" rules={[{ required: true }]}>
                                            <TextArea rows={3} placeholder="Describe the achievement..." />
                                        </Form.Item>
                                        <Form.Item {...field} name={[field.name, 'icon']} label="Icon (emoji or URL)">
                                            <Input size="large" placeholder="🏆 or icon URL" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} block>
                                                Remove Achievement
                                            </Button>
                                        )}
                                    </Card>
                                ))}
                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block style={{ marginTop: 8 }}>
                                    Add Achievement
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Submit Button */}
                <Form.Item style={{ marginTop: 32 }}>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        block
                        style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            border: 'none',
                            height: 48
                        }}
                    >
                        Save About Information
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
